import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { SlidersHorizontal, ChevronDown, Sparkles, Crown, UserCircle2, Crosshair, BookOpen, Scroll, Search as SearchIcon, LayoutGrid, X } from "lucide-react";
import { GPS } from "@/constants/testIds";
import { TRAVEL_MODES } from "@/lib/mapConstants";

// Top controls — Sir Eric's stack: ONE button, tap → tabs cascade DOWN the left side.
export const MapTopBar = ({ onSearch, onOpenFilters, activeCatLabel, travelMode, radius, onAsk, onCollect, onPogodex, onBoard, boardUnread, driver, onPlus, onJoin, onRecenter, children }) => {
  const [stackOpen, setStackOpen] = useState(false);
  const navigate = useNavigate();
  const tap = (fn) => () => { setStackOpen(false); fn?.(); };
  const sq = "rounded-xl h-11 w-11 flex items-center justify-center bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-md hover:bg-slate-600/90 transition-colors shrink-0";
  const tabs = [
    { id: "search", testid: "omnibox-open-btn", title: "Search active deals or locations", onClick: tap(onSearch), icon: <SearchIcon className="w-5 h-5 text-slate-100" /> },
    { id: "scroll", testid: "pog-board-open-btn", title: "THE POG SCROLL — word from AIR.POG HQ", onClick: tap(onBoard), icon: <Scroll className="w-5 h-5 text-slate-100" />, dot: boardUnread },
    { id: "pogodex", testid: "pogodex-open-btn", title: "POG'ODEX — merchant index", onClick: tap(onPogodex), icon: <BookOpen className="w-5 h-5 text-slate-100" /> },
    { id: "ask", testid: GPS.askBtn, title: "Ask AIR.POG", onClick: tap(onAsk), icon: <Sparkles className="w-5 h-5 text-slate-100" /> },
    { id: "hq", testid: "plus-account-btn", title: driver ? "Your POG HQ" : "AIR.POG Plus", onClick: tap(onPlus),
      icon: driver?.is_plus ? <Crown className="w-5 h-5 text-slate-100" /> : driver ? <UserCircle2 className="w-5 h-5 text-slate-100" /> : <Crown className="w-5 h-5 text-slate-100" /> },
    { id: "recenter", testid: GPS.recenterBtn, title: "Recenter on me", onClick: tap(onRecenter), icon: <Crosshair className="w-5 h-5 text-slate-100" /> },
  ];
  return (
    <div className="absolute top-0 inset-x-0 z-[1300] px-3 sm:px-5 pb-3 sm:pb-5 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-start justify-between gap-2 pointer-events-none">
      {/* LEFT: the ONE stack button — tap and the tabs cascade down the left side */}
      <div className="pointer-events-auto flex flex-col items-start gap-1.5 shrink-0">
        <div className="flex items-center gap-1.5">
        <button
          data-testid="map-stack-toggle"
          onClick={() => setStackOpen((v) => !v)}
          className={`rounded-xl h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-md transition-colors relative ${stackOpen ? "bg-slate-600" : "hover:bg-slate-600/90"}`}
          title={stackOpen ? "Close map tools" : "Map tools"}
        >
          {stackOpen ? <X className="w-5 h-5 text-slate-100" /> : <LayoutGrid className="w-5 h-5 text-slate-100" />}
          {boardUnread && !stackOpen && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-slate-900 animate-pulse" />}
        </button>
        <button
          data-testid="quick-find-fab"
          title="POG'KET WALLET ♥ — your saved merchants & apps"
          onClick={() => navigate("/wallet")}
          className="rounded-xl h-11 sm:h-12 w-11 sm:w-12 flex items-center justify-center bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-md hover:bg-slate-600/90 transition-colors"
        >
          <span className="text-rose-400 text-lg leading-none">♥</span>
        </button>
        </div>
        <AnimatePresence>
          {stackOpen && (
            <motion.div
              data-testid="map-stack-column"
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="flex flex-col gap-1.5 [&_[data-testid=more-menu]]:left-0 [&_[data-testid=more-menu]]:right-auto"
            >
              {tabs.map((b) => (
                <button key={b.id} data-testid={b.testid} onClick={b.onClick} title={b.title} className={`${sq} relative`}>
                  {b.icon}
                  {b.dot && <span data-testid="pog-board-unread-dot" className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-slate-900 animate-pulse" />}
                </button>
              ))}
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* RIGHT: Filters pill stays reachable */}
      <div className="pointer-events-auto shrink-0 flex items-center gap-1.5">
        <button
          data-testid={GPS.filterDropdown}
          onClick={onOpenFilters}
          className="rounded-xl h-11 sm:h-12 px-2.5 sm:px-4 flex items-center gap-1.5 sm:gap-2 min-w-0 max-w-[44vw] sm:max-w-none bg-slate-700/90 backdrop-blur-md border border-white/20 shadow-md hover:bg-slate-600/90 transition-colors"
        >
          <SlidersHorizontal className="w-4 h-4 text-slate-100 shrink-0" />
          <span className="text-sm font-bold text-slate-100 shrink-0">{radius}mi</span>
          <span className="text-slate-400 shrink-0 hidden sm:inline">·</span>
          <span className="font-heading font-bold text-sm truncate min-w-0 text-slate-100 hidden sm:inline">{activeCatLabel}</span>
          <span className="hidden sm:inline text-sm font-semibold text-slate-300 shrink-0">· {TRAVEL_MODES[travelMode].label}</span>
          <ChevronDown className="w-4 h-4 text-slate-300 shrink-0" />
        </button>
      </div>
    </div>
  );
};
